/**
 * Single source of truth for Voluum path-group conditions.
 * Mirrors `path_PathGroupConditionsResource` in `docs/voluum-api.json`.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  VOLUUM_CONDITION_KEYS,
  type VoluumConditionKey,
} from '@trafficpulse/shared';

import type { ConditionDescriptor } from './condition-types';

const DEVICE_TYPES = ['DESKTOP', 'TABLET', 'SMART_TV', 'MOBILE'] as const;
const CONNECTION_TYPES = [
  'BROADBAND',
  'WIRELESS',
  'XDSL',
  'CABLE',
  'DIALUP',
  'MOBILE',
  'SATELLITE',
  'UNKNOWN',
  'WIRED',
  'WIFI',
] as const;
const WEEKDAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;
const PROXY_TYPES = ['ANONYMOUS', 'CORPORATE', 'TRANSPARENT', 'UNKNOWN'] as const;

type Authorable = Omit<ConditionDescriptor, 'key' | 'authorable'> & {
  authorable?: boolean;
};

function entry(
  key: VoluumConditionKey,
  d: Authorable,
): ConditionDescriptor {
  return { key, authorable: d.authorable ?? true, ...d };
}

export const CONDITION_REGISTRY: readonly ConditionDescriptor[] = [
  entry('country', { label: 'Country', valueKind: 'country-codes', valueField: 'countries', predicateSupported: true }),
  entry('language', { label: 'Language', valueKind: 'language-codes', valueField: 'languages', predicateSupported: true }),
  entry('browser', { label: 'Browser', valueKind: 'string-list', valueField: 'browsers', predicateSupported: true }),
  entry('operatingSystem', { label: 'Operating system', valueKind: 'string-list', valueField: 'operatingSystems', predicateSupported: true }),
  entry('deviceType', { label: 'Device type', valueKind: 'enum-list', valueField: 'deviceTypes', enumValues: DEVICE_TYPES, predicateSupported: true }),
  entry('connectionType', { label: 'Connection type', valueKind: 'enum-list', valueField: 'types', enumValues: CONNECTION_TYPES, predicateSupported: true }),
  entry('weekdays', { label: 'Weekdays', valueKind: 'weekdays', valueField: 'weekdays', enumValues: WEEKDAYS, predicateSupported: true }),
  entry('timeOfDay', { label: 'Time of day', valueKind: 'time-of-day', valueField: '', predicateSupported: true, authorable: false }),
  entry('isp', { label: 'ISP', valueKind: 'string-list', valueField: 'names', predicateSupported: true }),
  entry('mobileCarrier', { label: 'Mobile carrier', valueKind: 'string-list', valueField: 'names', predicateSupported: true }),
  entry('brand', { label: 'Brand', valueKind: 'string-list', valueField: 'brands', predicateSupported: true }),
  entry('city', { label: 'City', valueKind: 'string-list', valueField: 'names', predicateSupported: true }),
  entry('region', { label: 'Region', valueKind: 'string-list', valueField: 'names', predicateSupported: true }),
  entry('referrer', { label: 'Referrer', valueKind: 'string-list', valueField: 'names', predicateSupported: true }),
  entry('referrerDomain', { label: 'Referrer domain', valueKind: 'string-list', valueField: 'names', predicateSupported: true }),
  entry('ip', { label: 'IP', valueKind: 'unsupported', valueField: 'ipV4Ranges', predicateSupported: true, authorable: false }),
  entry('proxy', { label: 'Proxy', valueKind: 'enum-list', valueField: 'proxyTypes', enumValues: PROXY_TYPES, predicateSupported: true }),
  entry('uniqueVisit', { label: 'Unique visit', valueKind: 'flag', valueField: '', predicateSupported: true }),
  entry('customVariable', { label: 'Custom variable', valueKind: 'unsupported', valueField: 'values', predicateSupported: false, authorable: false }),
];

const REGISTRY_BY_KEY = new Map(
  CONDITION_REGISTRY.map((d) => [d.key, d] as const),
);

export function getConditionDescriptor(
  key: string,
): ConditionDescriptor | undefined {
  return REGISTRY_BY_KEY.get(key);
}

export { VOLUUM_CONDITION_KEYS };
